/**
 * Excel Export Helpers
 *
 * Utility functions for post-processing AG Grid Excel exports.
 * Uses ExcelJS to export, add additional formatting, and combine sheets into a single auto-fitted sheet.
 */

import { CellFormat } from '@services/api/deckManagement/graphql/schema/API';
import ExcelJS from 'exceljs';

/**
 * Canonical cell format configuration — single source of truth for number formatting.
 *
 * Used by:
 * - AG Grid `valueFormatter` (grid display): reads `decimals`, `suffix`, and `scale`
 * - Excel export (shared package): reads `excelFmt` for `cell.numFmt`
 *
 * null/undefined/NONE all resolve to NUMBER (comma-separated, no decimals).
 * NOTE: Assumes percent values are stored as decimals in the backend (e.g., 0.12 = 12%) — `scale` handles the ×100 for display.
 */
export const CELL_FORMAT_CONFIG: Record<
  CellFormat,
  { decimals: number; suffix: string; excelFmt: string; scale: number }
> = {
  [CellFormat.NONE]: { decimals: 0, suffix: '', excelFmt: '#,##0;(#,##0); -', scale: 1 },
  [CellFormat.NUMBER]: { decimals: 0, suffix: '', excelFmt: '#,##0;(#,##0); -', scale: 1 },
  [CellFormat.PERCENT]: { decimals: 0, suffix: '%', excelFmt: '#,##0%;(#,##0%);\\n\\/\\m', scale: 100 },
  [CellFormat.PERCENT_1]: { decimals: 1, suffix: '%', excelFmt: '#,##0.0%;(#,##0.0%);\\n\\/\\m', scale: 100 },
  [CellFormat.PERCENT_2]: { decimals: 2, suffix: '%', excelFmt: '#,##0.00%;(#,##0.00%);\\n\\/\\m', scale: 100 },
  [CellFormat.BPS]: {
    decimals: 0,
    suffix: ' bps',
    excelFmt: '#,##0[$ bps-en-US];(#,##0[$ bps-en-US]);\\n\\/\\m',
    scale: 1,
  },
};

/**
 * Formats a numeric cell value for display based on the deckline's format_cell_default.
 * Returns empty string for null/undefined/empty values. Non-numeric values pass through as-is.
 */
export const formatCellValue = (value: unknown, format: CellFormat | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '';

  const num = typeof value === 'number' ? value : parseFloat(String(value));
  if (isNaN(num)) return String(value);

  const config = CELL_FORMAT_CONFIG[format || CellFormat.NUMBER];
  const scaled = num * config.scale;

  // Zero handling: NUMBER includes "-", PERCENT/BPS add "n/m"
  if (scaled === 0) {
    return format === CellFormat.NONE || format === CellFormat.NUMBER || !format ? '-' : 'n/m';
  }

  // Apply format to absolute value and localized to US English rules
  // TODO: Revisit if we ever need a different locale
  const abs = Math.abs(scaled);
  const formatted = abs.toLocaleString('en-US', {
    minimumFractionDigits: config.decimals,
    maximumFractionDigits: config.decimals,
  });

  // Negative handling: Add parentheses (financial convention for negatives)
  if (scaled < 0) {
    return `(${formatted}${config.suffix})`;
  }

  return `${formatted}${config.suffix}`;
};

/** Converts Excel column letters (e.g. "A", "AB") to 1-indexed column number */
const colLetterToNum = (letters: string): number => {
  let num = 0;
  for (let i = 0; i < letters.length; i += 1) {
    num = num * 26 + (letters.charCodeAt(i) - 64);
  }
  return num;
};

/** Parses a merge reference (e.g. "C5:F5") into numeric row/col ranges */
const parseMerge = (merge: string) => {
  const [start, end] = merge.split(':');
  return {
    startCol: colLetterToNum(start.replace(/\d+/g, '')),
    endCol: colLetterToNum(end.replace(/\d+/g, '')),
    startRow: parseInt(start.replace(/[A-Z]+/gi, ''), 10),
    endRow: parseInt(end.replace(/[A-Z]+/gi, ''), 10),
  };
};

/** Returns true if the cell in column 1 contains a numeric row number */
const isNumberedRow = (row: ExcelJS.Row): boolean => typeof row.getCell(1).value === 'number';

/**
 * Applies Excel numFmt to data cells in a numbered row based on the deckline's format_cell_default.
 * Ensures cells carry the correct number format for downstream number injection (ReportGenerationLambda),
 * regardless of whether the cell currently contains a value.
 *
 * @param row - The ExcelJS row to apply formatting to
 * @param dataRowIdx - Zero-based index into rowCellFormats (incremented per numbered row)
 * @param rowCellFormats - Array of CellFormat per data row (indexed by display row number - 1)
 * @param spacerPositions - Set of column positions occupied by spacer columns (to skip)
 */
const addCellNumFormat = (
  row: ExcelJS.Row,
  dataRowIdx: number,
  rowCellFormats: (CellFormat | null)[],
  spacerPositions: Set<number>,
): void => {
  if (rowCellFormats.length === 0) return;

  const format = rowCellFormats[dataRowIdx];
  if (!format) return;

  const config = CELL_FORMAT_CONFIG[format];
  if (!config) return;

  const targetRow = row;
  targetRow.eachCell({ includeEmpty: true }, (_cell, colNumber) => {
    // Skip row number (col 1) and KPI name (col 2); apply to data columns only
    if (colNumber <= 2 || spacerPositions.has(colNumber)) return;
    targetRow.getCell(colNumber).numFmt = config.excelFmt;
  });
};

/**
 * Processes an AG Grid Excel blob into a single sheet with auto-fitted column widths.
 * For multi-section pages, sheets are stacked vertically. For single-section pages,
 * the sheet is preserved as-is with column widths adjusted to content.
 *
 * @param blob - The Excel blob from AG Grid's getMultipleSheetsAsExcel()
 * @param sheetName - The name for the output sheet
 * @param blockSizes - Array of column counts per block (e.g. [4, 3, 4, 6]) for spacer insertion
 * @param rowCellFormats - Optional array of CellFormat per data row (indexed by display row number - 1) for numFmt application
 * @returns A new Blob with combined content and auto-fitted columns
 */
export const processExcelExport = async (
  blob: Blob,
  sheetName: string,
  blockSizes: number[] = [],
  rowCellFormats: (CellFormat | null)[] = [],
): Promise<Blob> => {
  const arrayBuffer = await blob.arrayBuffer();

  // Read the AG Grid workbook
  const sourceWorkbook = new ExcelJS.Workbook();
  await sourceWorkbook.xlsx.load(arrayBuffer);

  // Create a new workbook with a single combined sheet for export
  const outputWorkbook = new ExcelJS.Workbook();
  const outputSheet = outputWorkbook.addWorksheet(sheetName);

  // Compute spacer column positions (in output space) inserted between blocks
  // For blockSizes [4, 3, 4, 6]: spacers land at output cols 7, 11, 16
  const spacerPositions = new Set<number>();
  if (blockSizes.length > 1) {
    let col = 2; // first 2 cols are row number + KPI name
    for (let i = 0; i < blockSizes.length - 1; i += 1) {
      col += blockSizes[i] + 1; // +1 for the spacer column itself
      spacerPositions.add(col);
    }
  }

  // Map source column to output column, shifting data columns past spacer positions
  const colMap = (srcCol: number): number => {
    if (spacerPositions.size === 0 || srcCol <= 2) return srcCol;
    let offset = 0;
    spacerPositions.forEach((spacerCol) => {
      if (srcCol + offset >= spacerCol) offset += 1;
    });
    return srcCol + offset;
  };

  // For tracking row positioning across sheets
  let currentRow = 1;
  // Stack sheets vertically by copying rows and adjusting merged cell references
  sourceWorkbook.eachSheet((sourceSheet) => {
    const rowOffset = currentRow - 1;

    // Copy rows from source sheet into the combined output
    sourceSheet.eachRow({ includeEmpty: true }, (row) => {
      const outputRow = outputSheet.getRow(currentRow);

      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const outputCell = outputRow.getCell(colMap(colNumber));
        outputCell.value = cell.value;

        // Preserve styling
        if (cell.font) outputCell.font = { ...cell.font };
        if (cell.alignment) outputCell.alignment = { ...cell.alignment };
        if (cell.fill && cell.fill.type) outputCell.fill = { ...cell.fill } as ExcelJS.Fill;
        if (cell.border) outputCell.border = { ...cell.border };
        if (cell.numFmt) outputCell.numFmt = cell.numFmt;
      });

      if (row.height) outputRow.height = row.height;
      outputRow.commit();
      currentRow += 1;
    });

    // Reposition merged cells with column mapping and row offset
    sourceSheet.model.merges?.forEach((merge: string) => {
      const { startCol, endCol, startRow, endRow } = parseMerge(merge);
      outputSheet.mergeCells(startRow + rowOffset, colMap(startCol), endRow + rowOffset, colMap(endCol));
    });
  });

  // Merge granularity cell vertically down 1 row per section
  // Row 2 of each section contains the granularity label (already merged A:B horizontally)
  // Extend it to also span row 3 vertically (which AGGrid export could not support alone)
  let sectionStartRow = 1;
  sourceWorkbook.eachSheet((sourceSheet) => {
    const granularityRow = sectionStartRow + 1;
    try {
      outputSheet.unMergeCells(`A${granularityRow}:B${granularityRow}`);
      outputSheet.mergeCells(`A${granularityRow}:B${granularityRow + 1}`);
    } catch {
      // Skip if cells are not in expected merge state
    }
    sectionStartRow += sourceSheet.rowCount;
  });

  // Find the first row with a row number (start of data area includes section headers before it)
  // Also applies numFmt to data cells while iterating (avoids a second pass)
  let firstNumberedRow = 0;
  let dataRowIdx = 0;
  outputSheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    if (!isNumberedRow(row)) return;
    if (!firstNumberedRow) firstNumberedRow = rowNumber;
    addCellNumFormat(row, dataRowIdx, rowCellFormats, spacerPositions);
    dataRowIdx += 1;
  });
  // Data area starts 2 rows before first numbered row (section header + blank separator)
  const dataAreaStart = Math.max(firstNumberedRow - 2, 1);

  // Set spacer column widths and apply borders to suppress gridlines (matching reference deck)
  const whiteBorder: ExcelJS.Border = { style: 'medium', color: { argb: 'FFFFFFFF' } };
  const greyBorder: ExcelJS.Border = { style: 'medium', color: { argb: 'FFC0BFC0' } };
  spacerPositions.forEach((pos) => {
    outputSheet.getColumn(pos).width = 2.6;
    for (let r = 1; r <= outputSheet.rowCount; r += 1) {
      const cell = outputSheet.getRow(r).getCell(pos);
      if (r === 5) {
        // Block header row — grey left/right borders as visible separator between block names
        cell.border = { left: greyBorder, right: greyBorder, bottom: whiteBorder };
      } else if (r < dataAreaStart) {
        // Header rows — white borders on all sides to suppress gridlines
        cell.border = { left: whiteBorder, right: whiteBorder, bottom: whiteBorder };
      } else {
        // Data area (numbered + section headers + blank separators) — only white bottom
        cell.border = { bottom: whiteBorder };
      }
    }
  });

  const whiteTopBorder: ExcelJS.Border = { style: 'medium', color: { argb: 'FFFFFFFF' } };

  // Apply white fill to suppress gridlines on non-data rows (headers, blank separators)
  // Rows with a row number in col 1 and footnote rows retain gridlines
  const whiteFill: ExcelJS.Fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFFFFF' } };
  let lastNumberedRow = 0;
  outputSheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    if (isNumberedRow(row)) lastNumberedRow = rowNumber;
  });

  // Suppress top gridline on first footnote row
  const footnoteRow = outputSheet.getRow(lastNumberedRow + 2);
  if (footnoteRow) {
    const cell = footnoteRow.getCell(2);
    if (!cell.border?.top) {
      cell.border = { ...cell.border, top: whiteTopBorder };
    }
  }

  outputSheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    if (isNumberedRow(row)) return;
    if (rowNumber > lastNumberedRow + 1) return; // footnotes start after blank line
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      // Col 2: skip white fill if row has KPI content or is adjacent to numbered data rows
      if (colNumber === 2 && rowNumber >= dataAreaStart) {
        if (row.getCell(2).value) return; // section header with text
        const prevHasNumber = rowNumber > 1 && isNumberedRow(outputSheet.getRow(rowNumber - 1));
        const nextHasNumber = rowNumber < outputSheet.rowCount && isNumberedRow(outputSheet.getRow(rowNumber + 1));
        if (prevHasNumber || nextHasNumber) return;
      }
      if (spacerPositions.has(colNumber)) return;
      if (!cell.fill || cell.fill.type !== 'pattern' || cell.fill.pattern === 'none') {
        outputSheet.getRow(rowNumber).getCell(colNumber).fill = whiteFill;
      }
    });
  });

  // Add grey bottom border on numbered data rows (row separator matching reference deck)
  // Skip last row in each section (followed by non-numbered row or blank separator)
  const greyBottomBorder: ExcelJS.Border = { style: 'medium', color: { argb: 'FF808080' } };
  outputSheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    if (!isNumberedRow(row)) return;
    const nextRow = outputSheet.getRow(rowNumber + 1);
    if (!isNumberedRow(nextRow)) return;
    if (!nextRow.getCell(2).value) return; // blank separator — skip
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      if (colNumber === 1 || spacerPositions.has(colNumber)) return;
      if (!cell.border?.bottom) {
        outputSheet.getRow(rowNumber).getCell(colNumber).border = { ...cell.border, bottom: greyBottomBorder };
      }
    });
  });

  // Add white top border on first numbered row of each section (only if it received a grey bottom border)
  outputSheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    if (!isNumberedRow(row)) return;
    const prevRow = rowNumber > 1 ? outputSheet.getRow(rowNumber - 1) : null;
    if (!prevRow || isNumberedRow(prevRow)) return;
    const cell = row.getCell(2);
    if (cell.border?.bottom && !cell.border?.top) {
      cell.border = { ...cell.border, top: whiteTopBorder };
    }
  });

  // Auto-fit column widths based on cell content
  // Skip all cells within merges spanning more than 5 columns (major report headers, footnotes, etc.)

  // Identify wide merged cells (spanning more than 5 columns) to exclude from auto-width calculation
  const wideMergeCellAddresses = new Set<string>();
  outputSheet.model.merges?.forEach((merge: string) => {
    const { startCol, endCol, startRow, endRow } = parseMerge(merge);
    if (endCol - startCol > 5) {
      for (let r = startRow; r <= endRow; r += 1) {
        for (let c = startCol; c <= endCol; c += 1) {
          wideMergeCellAddresses.add(`${r}:${c}`);
        }
      }
    }
  });

  // Width units per character scaling for Arial size 30 (calibrated from Excel: ~2.75 units/char)
  const charWidthFactor = 2.75;

  outputSheet.columns.forEach((_column, colIdx) => {
    const colNum = colIdx + 1;
    if (spacerPositions.has(colNum)) return;
    let maxCellWidth = 0;
    const currColumn = outputSheet.getColumn(colNum);
    currColumn.eachCell?.({ includeEmpty: true }, (cell, rowNumber) => {
      if (wideMergeCellAddresses.has(`${rowNumber}:${colNum}`)) return;
      const cellLength = cell.value ? cell.value.toString().length : 0;
      maxCellWidth = Math.max(maxCellWidth, cellLength);
    });
    if (colIdx < 2) {
      // Allows for Row number and KPI name columns — auto-fit to content
      currColumn.width = maxCellWidth < 4 ? 10 : Math.ceil(maxCellWidth * charWidthFactor);
    } else {
      // Data columns — fixed width matching scenario column expectations
      currColumn.width = 33;
    }
  });

  // Generate output blob to be used in export
  const outputBuffer = await outputWorkbook.xlsx.writeBuffer();
  return new Blob([outputBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};

/**
 * Triggers a browser file download from a Blob
 *
 * @param blob - The file blob to download
 * @param fileName - The desired file name
 */
export const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};
